import React, { useCallback, useContext, useMemo } from 'react'

import SlideDown from 'components/Common/SlideDown'
import useToggle from 'hooks/useToggle'
import TextButton from 'components/Luxkit/Button/TextButton'
import DepartureCard from './DepartureCard'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { getDeparturesCheapestPurchasableOptions, getDeparturesOccupanciesPriceBreakdowns } from 'selectors/TourV2/tourV2OfferPageDeparturesSelectors'
import DropdownChevron from 'components/Luxkit/Dropdown/DropdownChevron'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { connect } from 'react-redux'
import { getSupportContact } from 'selectors/supportContactSelector'
import { MONTH_NAMES } from 'constants/dates'
import { sortBy, take } from 'lib/arrayUtils'
import TextLink from 'components/Luxkit/TextLink'
import ModalContext from 'contexts/ModalContext'
import PrivateRequestFormModal from 'components/OfferPage/TourV2/PrivateRequest/PrivateRequestFormModal'

const DepartureCardsList = styled.div`
  display: flex;
  flex-direction: column;

  > .departure-card + .departure-card {
    margin-top: ${rem(16)};
  }
`

const SoldOutMessagePane = styled.div`
  background: ${props => props.theme.palette.neutral.eight};
  padding: ${rem(16)} ${rem(20)};
  border: 1px solid ${props => props.theme.palette.neutral.five};
  display: flex;
  gap: ${rem(4)};
  border-radius: ${props => props.theme.borderRadius.large};
  flex-direction: column;
  align-items: start;

  ${mediaQueryUp.tablet} {
    padding: ${rem(16)} ${rem(32)};
  }
`

const SoldOutMessagePaneContent = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(4)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1.5fr 1fr;
  }
`

const SoldOutsToggleButton = styled(TextButton)`
  margin: ${rem(8)} 0;
`

const ContactGroup = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(16)};
  justify-items: center;
  margin-top: ${rem(16)};

  Button {
    width: 100%;
  }

  ${mediaQueryUp.tablet} {
    display: flex;
    align-items: center;
    width: 100%;
    justify-content: end;
    flex-direction: row-reverse;
    margin-top: 0;

    Button {
      width: auto;
    }
  }
`

interface MappedStateProps {
  supportContact: App.GeoContacts
}

interface MappedOfferPageStateProps {
  departuresCheapestPurchasableOptions: Map<Tours.V2OfferDeparture, Tours.V2OfferPurchasableOption>
  departuresOccupanciesPriceBreakdowns: Map<Tours.V2OfferDeparture, Map<App.Occupants, Map<Tours.V2OfferPurchasableOption, Tours.V2OfferDeparturePriceBreakdown>>>
  selectedVariation?: Tours.V2OfferVariation
  offer: Tours.V2Offer
}

interface Props {
  monthIndex: App.MonthIndex
  isExpanded?: boolean
  availableDepartures: Tours.V2OfferDeparture[]
  soldOutDepartures: Tours.V2OfferDeparture[]
  numberOfDeparturesToDisplay?: number
  onDepartureSelect: (departure: Tours.V2OfferDeparture) => void
  onDepartureGuaranteedLabelClick?: () => void
}

function TourV2MonthDepartures(props: Props & MappedStateProps & MappedOfferPageStateProps) {
  const {
    availableDepartures,
    departuresCheapestPurchasableOptions,
    departuresOccupanciesPriceBreakdowns,
    monthIndex,
    numberOfDeparturesToDisplay,
    offer,
    selectedVariation,
    soldOutDepartures,
    supportContact,
    onDepartureSelect,
    onDepartureGuaranteedLabelClick,
  } = props

  const [areSoldOutsExpanded, toggleSoldOuts] = useToggle(false)

  const displayableAvailableDepartures = useMemo(() => sortBy(
    isNaN(numberOfDeparturesToDisplay) ? availableDepartures : take(availableDepartures, numberOfDeparturesToDisplay),
    (departure) => departure.startDate,
    'asc',
  ), [numberOfDeparturesToDisplay, availableDepartures])

  const soldOutDeparturesCopy = useMemo(() =>
    selectedVariation?.isPrivateRequest && soldOutDepartures.length > 0 ? 'Have a group that\'s ready to go and like this itinerary? Request a private tour, request a call back for more options or try another month.' : 'Try a different month or give our team a call for more tour options.'
  , [selectedVariation, soldOutDepartures])

  const contact = supportContact[supportContact.default]
  const showModal = useContext(ModalContext)
  const toggleModal = useCallback(() => {
    showModal(<PrivateRequestFormModal variations={offer.variations} tourId={offer.id} />)
  }, [showModal, offer])

  return <DepartureCardsList>
    {displayableAvailableDepartures.length !== 0 && displayableAvailableDepartures.map((departure) => <DepartureCard
      key={departure.id}
      className="departure-card"
      departure={departure}
      endLocation={selectedVariation?.endLocation}
      occupanciesPriceBreakdowns={departuresOccupanciesPriceBreakdowns.get(departure)}
      cheapestPurchasableOption={departuresCheapestPurchasableOptions.get(departure)}
      startLocation={selectedVariation?.startLocation}
      onDepartureSelect={onDepartureSelect}
      onGuaranteedLabelClick={onDepartureGuaranteedLabelClick}
    />)}

    {availableDepartures.length === 0 && <SoldOutMessagePane>
      <SoldOutMessagePaneContent>
        <div>
          <BodyText variant="large" weight="bold">{soldOutDepartures.length > 0 ? 'All departures are sold out.' : 'No departures' }</BodyText>
          <BodyText variant="medium">{soldOutDeparturesCopy}</BodyText>
        </div>
        <ContactGroup>
          {selectedVariation?.isPrivateRequest &&
            <TextButton kind="secondary" onClick={toggleModal}>Request private tour</TextButton>
          }
          <TextLink
            size="medium"
            weight="regular"
            href={`tel:${contact.number}`}>
            Call on {contact.humanReadable}
          </TextLink>
        </ContactGroup>
      </SoldOutMessagePaneContent>
    </SoldOutMessagePane>}

    {soldOutDepartures.length > 0 && <>
      <div>
        <SoldOutsToggleButton
          kind="tertiary"
          outdent="left"
          endIcon={<DropdownChevron open={areSoldOutsExpanded} />}
          onClick={toggleSoldOuts}
        >
          {areSoldOutsExpanded ? 'Hide' : 'View'} sold out dates in {MONTH_NAMES[monthIndex]}
        </SoldOutsToggleButton>
      </div>

      <SlideDown show={areSoldOutsExpanded} animateOpacity>
        <DepartureCardsList>
          {soldOutDepartures.map((departure) => <DepartureCard
            key={departure.id}
            className="departure-card"
            departure={departure}
            endLocation={selectedVariation?.endLocation}
            occupanciesPriceBreakdowns={departuresOccupanciesPriceBreakdowns.get(departure)}
            cheapestPurchasableOption={departuresCheapestPurchasableOptions.get(departure)}
            startLocation={selectedVariation?.startLocation}
            onDepartureSelect={onDepartureSelect}
            onGuaranteedLabelClick={onDepartureGuaranteedLabelClick}
          />)}
        </DepartureCardsList>
      </SlideDown>
    </>}
  </DepartureCardsList>
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => ({
  supportContact: getSupportContact(state),
}))(withOfferPageState<MappedOfferPageStateProps>((offerPageState) => ({
  departuresCheapestPurchasableOptions: getDeparturesCheapestPurchasableOptions(offerPageState),
  departuresOccupanciesPriceBreakdowns: getDeparturesOccupanciesPriceBreakdowns(offerPageState),
  selectedVariation: offerPageState.selectedTourV2OfferVariation,
  offer: offerPageState.tourV2Offer,
}))(TourV2MonthDepartures))
